import { templateTableSearchAtom } from '@/atoms/templateLibraryAtoms';
import { useBlockErrorTracking } from '@/hooks/useEvidenceAnalytics';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  Loader2,
  Search,
} from 'lucide-react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { TemplateItem } from '@heidi/templates/contracts';

// Above this many results the list scrolls instead of growing unbounded,
// keeping the block to roughly five rows tall (mirrors SessionsByDateBlock).
const MAX_VISIBLE_TEMPLATES = 5;

interface TemplateSearchBlockProps {
  /** Whether this is a user's own templates search or community gallery search */
  variant: 'own' | 'community';
  status: 'searching' | 'completed' | 'error';
  /**
   * Whether this block represents a true search ('search', the default) or a
   * fetch of the active session's template + preferences ('fetch'). Fetch mode
   * uses different completed-state copy because there is no result count to show.
   */
  purpose?: 'search' | 'fetch';
  query?: string;
  totalResults?: number;
  /**
   * The matched templates for a completed search. When present (and not a
   * fetch), the block renders a collapsible list of results; otherwise it
   * falls back to the summary row.
   */
  templates?: TemplateItem[];
  /** Only used when purpose='fetch'. */
  templateAvailable?: boolean;
  /** Only used when purpose='fetch'. */
  templateName?: string | null;
  error?: string;
  className?: string;
}

/** Badge styling for a template's source ("custom" / "team" / "system"). */
const getSourceStyle = (source: string): string => {
  const normalized = source.toLowerCase();
  if (normalized === 'custom') return 'bg-validation-info-muted text-validation-info';
  if (normalized === 'team') return 'bg-brand-primary/10 text-brand-primary';
  return 'bg-surface-disabled text-text-disabled';
};

/**
 * TemplateSearchBlock - Displays template search status and results summary
 *
 * Two variants:
 * - 'own': Searching user's personal/team templates
 * - 'community': Searching the Heidi community gallery
 *
 * Visual states:
 * - SEARCHING: Loading spinner with query context
 * - COMPLETED: Results summary badge
 * - ERROR: Error message
 */
export const TemplateSearchBlock = ({
  variant,
  status,
  purpose = 'search',
  query,
  totalResults,
  templates,
  templateAvailable,
  templateName,
  error,
  className,
}: TemplateSearchBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const intl = useIntl();
  const router = useRouter();
  const setTemplateSearch = useSetAtom(templateTableSearchAtom);
  // Result list is collapsed by default — it's a process step, not the answer.
  const [isOpen, setIsOpen] = useState(false);
  useBlockErrorTracking('template_search', status, error);

  // "Open" a template by sending the user to their template library, pre-filtered
  // to the chosen template's name so they can view/edit it.
  const handleOpenTemplate = (name: string) => {
    setTemplateSearch(name);
    void router.push('/scribe/my-templates');
  };

  const Icon = variant === 'community' ? Globe : FileText;
  const label = variant === 'community' ? t('chat.templateSearch.communityTemplates' as TranslationKey) : t('chat.templateSearch.myTemplates' as TranslationKey);

  // Searching state
  if (status === 'searching') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-secondary">
          <Icon className="size-4 text-text-tertiary" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="font-medium text-text-primary">{label}</TypographyP2>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="min-w-0 truncate text-text-tertiary">
              {query ? t('chat.templateSearch.searchingForQuery' as TranslationKey, { query }) : t('chat.templateSearch.searching' as TranslationKey)}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
      >
        <FramedIcon size="sm" state="error" icon={<AlertCircle />} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="font-medium text-text-primary">{label}</TypographyP2>
          <TypographyCaption className="text-validation-destructive">
            {error || t('chat.templateSearch.errorDefault' as TranslationKey)}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Completed state
  // Fetch mode (single-template fetch) and search mode (multi-template search)
  // have different completion semantics: search reports a count, fetch reports
  // which template (if any) was loaded.
  const isFetch = purpose === 'fetch';
  // When we have the actual result rows, trust their length for the summary so
  // the caption ("Found N") can never disagree with the rendered list (rows
  // missing an id/name are dropped upstream). Fall back to the BE-reported
  // count when no rows are passed (e.g. the legacy summary-only render).
  const resultCount = !isFetch && templates ? templates.length : totalResults;
  const highlight = isFetch
    ? templateAvailable !== false
    : Boolean(resultCount && resultCount > 0);

  let completedCaption: string;
  if (isFetch) {
    if (templateAvailable === false) {
      completedCaption = intl.formatMessage({
        defaultMessage: 'No template set', id: 'evtcfO5fhZ',
        description: 'Caption shown when Ask Heidi fetches the active session template and the user has not configured one',
      });
    } else if (templateName) {
      completedCaption = intl.formatMessage(
        {
          defaultMessage: 'Loaded: {name}', id: 'b7KchIMN7v',
          description: 'Caption shown when Ask Heidi successfully fetched the active session template, with the template name',
        },
        { name: templateName }
      );
    } else {
      completedCaption = intl.formatMessage({
        defaultMessage: 'Template loaded', id: 'sGv+/oaJFC',
        description: 'Caption shown when Ask Heidi successfully fetched the active session template (name unavailable)',
      });
    }
  } else if (resultCount !== undefined && resultCount > 0) {
    completedCaption = query
      ? t('chat.templateSearch.foundCountWithQuery' as TranslationKey, { count: resultCount, query })
      : t('chat.templateSearch.foundCount' as TranslationKey, { count: resultCount });
  } else {
    completedCaption = query
      ? t('chat.templateSearch.noResultsWithQuery' as TranslationKey, { query })
      : t('chat.templateSearch.noResults' as TranslationKey);
  }

  // Completed search with a result list → collapsible accordion of templates.
  // Fetch mode (single-template) and empty results keep the summary row below.
  if (!isFetch && templates && templates.length > 0) {
    const templateList = (
      <div className="flex flex-col divide-y divide-border/30">
        {templates.map((template) => {
          const subtitle =
            template.description ||
            template.category?.toLowerCase().replace(/_/g, ' ') ||
            '';

          return (
            <button
              type="button"
              key={template.template_id}
              onClick={() => handleOpenTemplate(template.name)}
              className="group flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
            >
              <FileText className="size-4 shrink-0 text-text-tertiary group-hover:text-brand-primary" />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-medium text-text-primary">
                    {template.name}
                  </span>
                  {template.source && (
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize leading-none',
                        getSourceStyle(template.source)
                      )}
                    >
                      {template.source}
                    </span>
                  )}
                </div>
                {subtitle && (
                  <TypographyCaption className="line-clamp-1 text-text-tertiary">
                    {subtitle}
                  </TypographyCaption>
                )}
              </div>
              <ChevronRight className="size-4 shrink-0 text-text-tertiary opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" />
            </button>
          );
        })}
      </div>
    );

    return (
      <div
        className={cn(
          'w-full overflow-hidden rounded-lg border border-border/50 bg-surface',
          'transition-all duration-200 animate-in fade-in',
          className
        )}
      >
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          className={cn(
            'flex w-full items-center gap-3 bg-background-secondary/50 px-4 py-3 text-left transition-colors hover:bg-background-secondary/80',
            isOpen && 'border-b border-border/40'
          )}
        >
          <div
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-md',
              highlight ? 'bg-brand-primary/10' : 'bg-background-secondary'
            )}
          >
            {highlight ? (
              <Search className="size-4 text-brand-primary" />
            ) : (
              <Icon className="size-4 text-text-tertiary" />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <TypographyP2 className="font-medium text-text-primary">{label}</TypographyP2>
            <TypographyCaption className="text-text-tertiary">
              {completedCaption}
            </TypographyCaption>
          </div>
          <ChevronDown
            className={cn(
              'size-5 shrink-0 text-text-tertiary transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen &&
          (templates.length > MAX_VISIBLE_TEMPLATES ? (
            <ScrollArea scrollbarVariant="minimal" className="max-h-[18rem]">
              {templateList}
            </ScrollArea>
          ) : (
            templateList
          ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-border/50 bg-surface p-4',
        'transition-all duration-200 animate-in fade-in',
        className
      )}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md',
          highlight ? 'bg-brand-primary/10' : 'bg-background-secondary'
        )}
      >
        {highlight && !isFetch ? (
          <Search className="text-brand-primary size-4" />
        ) : (
          <Icon className={cn('size-4', highlight ? 'text-brand-primary' : 'text-text-tertiary')} />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyP2 className="font-medium text-text-primary">{label}</TypographyP2>
        <TypographyCaption className="text-text-tertiary">{completedCaption}</TypographyCaption>
      </div>
    </div>
  );
};
